import { Investors } from '@components/book-trial-class-jp/investors'
import CodingCertificate from '@components/common/coding-certificate'
import CodingForStudents from '@components/common/coding-for-students'
import Footer from '@components/common/footer'
import Navbar from '@components/common/navbar'
import Activities from '@components/course-landing-page/activities'
import ClassCurriculum from '@components/course-landing-page/class-curriculum'
import CourseDescription from '@components/course-landing-page/course-description'
import CourseFeatures from '@components/course-landing-page/course-features'
import Faq from '@components/course-landing-page/faq'
import FirstFold from '@components/course-landing-page/first-fold'
import FooterBanner from '@components/course-landing-page/footer-banner'
import RecommendedCourses from '@components/course-landing-page/recommended-courses'
import StemAccredited from '@components/course-landing-page/stem-accredited'
import Teachers from '@components/course-landing-page/teachers'
import { COURSE_INFO } from '@components/locale-pages/home/courses'
import VideoTestimonial from '@components/locale-pages/home/video-testimonial'
import useStoreUTMParams from '@hooks/useStoreUTMParam'
import { COURSE_NAMES, FAQ } from '@lib/data/course-landing-page'
import { VIDEO_TESTIMONIAL } from '@lib/data/summer-coding-camp'
import { Trans } from '@lingui/macro'
import { CourseJsonLd, FAQPageJsonLd, NextSeo } from 'next-seo'
import dynamic from 'next/dynamic'
import CodingalProductBlog from '@components/blog/product-blogs'
import { useState } from 'react'
import { formatSeoConfig } from '@lib/seo'
import TrustpilotReview from '@components/locale-pages/home/trustpilot-review'
import ContentCard from '@components/course-landing-page/content-card'

const CourseCard = dynamic(() =>
  import('@components/platform-course-landing-page/course-card')
)

const { GOOGLE_COLAB } = COURSE_NAMES
const urlQuery = `?course=${COURSE_INFO['aigc'].trialCourseQuery}`

export const GoogleColabSeo = formatSeoConfig({
  title: 'Google Colab for Kids | Learn AI & Python Coding',
  description:
    'Python and AI for kids via Google Colab, taught by expert instructors. No setup needed - code in-browser with a structured curriculum. Try a free lesson.',
  url: 'https://www.codingal.com/courses/google-colab-for-kids/',
  metaImage:
    'https://fastly.codingal.com/images/courses/og/codingal-dark-v3.jpg',
})
const GoogleColab = () => {
  const [shouldUpdateCourseCard, setShouldUpdateCourseCard] = useState(false)

  const handleHideCourseCard = (value: boolean) => {
    setShouldUpdateCourseCard(value)
  }

  useStoreUTMParams()

  return (
    <>
      <NextSeo {...GoogleColabSeo} />
      <FAQPageJsonLd
        mainEntity={FAQ[GOOGLE_COLAB].map(
          ({ acceptedAnswerText, questionName }) => ({
            acceptedAnswerText: acceptedAnswerText,
            questionName: questionName,
          })
        )}
      />
      <CourseJsonLd
        courseName="Learn Google Colab with the Top AI & coding Course Instructors for Kids and Teens"
        description="Google Colab is a cloud-based coding environment that allows kids to write and run Python code right in their browser without the need to install anything! It's like a digital notebook where students can combine code, visuals, and text in one place. In our courses, kids use Google Colab to learn AI concepts, build machine learning models, visualize data, and create exciting projects. It’s safe, accessible, and perfect for young learners stepping into the world of technology."
        provider={{
          '@type': 'Organization',
          name: 'Codingal',
        }}
        offers={{
          '@type': 'Offer',
          category: 'Paid',
        }}
        hasCourseInstance={{
          '@type': 'CourseInstance',
          courseMode: 'Online',
          courseWorkload: 'PT44H',
        }}
      />
      <Navbar tryFreeClassLink={`/register/${urlQuery}`} />
      <FirstFold
        getShouldHideCourseCard={handleHideCourseCard}
        courseName={GOOGLE_COLAB}
        urlQuery={urlQuery}
        isPlatformPage
      />
      <div className="container flex justify-between pt-10 mx-auto px-5 lg:px-10 text-grey">
        <div className="pb-10 md:w-4/5 md:pr-4 max-w-3xl">
          <CourseDescription courseName={GOOGLE_COLAB} />
          <CourseFeatures
            courseTitle={GOOGLE_COLAB}
            title={
              <Trans>
                An engaging Google Colab programming lessons for kids
              </Trans>
            }
          />
          <ClassCurriculum
            courseName={GOOGLE_COLAB}
            title="Our Google Colab Class Curriculum"
            description={
              <>
                <Trans>
                  Our curriculum integrates Google Colab across multiple modules
                  to make learning interactive and professional. Students start
                  with data analysis using Pandas and Matplotlib, then explore
                  advanced concepts like Machine Learning, Deep Learning, and
                  Computer Vision—all within the Colab environment. Every
                  capstone project and activity is designed to simulate
                  real-world scenarios using this industry tool.
                </Trans>
              </>
            }
          />
          <Activities courseName={GOOGLE_COLAB} />
          <Teachers
            urlQuery={urlQuery}
            title={
              <Trans>
                Learn from experienced teachers. Our hand-picked AI & coding
                tutors ensure the best learning experience
              </Trans>
            }
            description={
              <Trans>
                <p>
                  Our instructors bring deep expertise in Computer Science and
                  complete rigorous training before they teach; reflected in our
                  exceptional Net Promoter Score (NPS) of 86. Students and
                  parents consistently rate our teachers 4.9 out of 5, and our
                  curriculum earns a 4.8 out of 5. We’re proud to have a faculty
                  that’s 91% female and 9% male, creating an inclusive,
                  encouraging learning environment.
                </p>
                <p>
                  Each tutor is trained to understand individual student needs
                  and deliver personalized support—even in group settings. Plus,
                  students have 24/7 access to expert tutors for instant doubt
                  resolution, anytime they need help.
                </p>
              </Trans>
            }
          />
          <ContentCard
            title={
              <Trans>
                The best place for kids to learn the basics and fundamentals of
                Google Colab
              </Trans>
            }
            description={
              <Trans>
                <span className="block">
                  At Codingal, we believe every child deserves a solid
                  foundation in technology. Our structured approach starts with
                  easy-to-follow lessons in Google Colab, guiding kids from
                  basic Python syntax to advanced AI models. With personalized
                  support, kids gain confidence and mastery step-by-step, making
                  even the most complex topics feel simple and fun.
                </span>
              </Trans>
            }
            courseName={GOOGLE_COLAB}
          />
          <StemAccredited
            urlQuery={urlQuery}
            heading="STEM.org accredited educational experience and certificates"
            description={
              <>
                <p>
                  At Codingal, your child masters Data Science with Google Colab
                  through a STEM.org–accredited program built on proven
                  educational frameworks like Bloom’s Taxonomy, BIDE, and STEAM.
                  Our curriculum is aligned with Common Core, state standards,
                  AP Computer Science goals, and the K–12 Computer Science
                  Framework ensuring learning that’s both age-appropriate and
                  college-ready. Through hands-on, project-based learning,
                  students create real-world apps like temperature converters
                  and scientific calculators, applying math, science, and
                  engineering logic to solve everyday problems. Backed by
                  STEM.org’s rigorous audits, every Data Science lesson lays a
                  strong, future-proof foundation in STEM.
                </p>
              </>
            }
          />
          <CodingCertificate
            title="Unlock your skills and earn your Google Colab certifications"
            description={
              <>
                In Codingal’s Grandmaster Data Science track, students in grades
                6–12 progress through two modules; JavaScript Fundamentals and
                Advanced JavaScript starting with core concepts and advancing to
                real-world web development. They learn everything from variables
                and functions to APIs, async/await, and responsive design,
                turning lessons into polished apps and interactive websites.
                Upon successful completion, students earn the STEM.org-certified
                AI & Coding Grandmaster Certificate; a valuable credential for
                academic portfolios, resumes, and college applications. With
                industry-relevant skills and a recognized certificate, learners
                graduate with confidence, a strong project portfolio, and a
                competitive edge for future studies and tech careers.
              </>
            }
            imagePath="/images/courses/ai-and-ml/cetrificate"
            imageWidth={230}
            imageHeight={195}
          />
          <CodingForStudents
            title="Our Google Colab classes are appropriate for middle school & high school students"
            description={
              <>
                At Codingal, our Data Science curriculum guides middle and high
                school students from writing their first line of Python code
                like print(&ldquo;Hello, World&rdquo;) to analyzing real-world
                datasets and building intelligent, data-driven applications. We
                start with beginner-friendly challenges that introduce core
                concepts such as variables, loops, and functions through
                engaging, hands-on notebooks. As students progress, they delve
                into advanced topics like data visualization, conditional logic,
                and statistical analysis, ensuring a smooth, steady learning
                curve. With every project, they reinforce their understanding of
                data structures, analytical thinking, and problem-solving,
                gaining the confidence to tackle increasingly complex data
                science challenges.
              </>
            }
            imageWidth={235}
            imageHeight={241}
            imagePath="/images/courses/ai-and-ml/ai-course-students"
          />
          <VideoTestimonial
            isCoursepage
            title={
              <Trans>
                What students and parents have to say about our training program
              </Trans>
            }
            video_testimonial={VIDEO_TESTIMONIAL}
          />
          <TrustpilotReview isCoursepage urlQuery={urlQuery} />
          <Investors isCoursePage urlQuery={urlQuery} />
          <RecommendedCourses courseCodes={['ds']} />
          <Faq
            courseName={GOOGLE_COLAB}
            title="Frequently Asked Questions (FAQs) About Google Colab"
          />
          <CodingalProductBlog
            urlQuery={urlQuery}
            isCoursePage
            title="Latest from Our Blog"
          />
        </div>
        <div className="hidden ml-4 md:block w-80">
          <div className="pb-5 sticky top-0 pt-5 z-40 w-80">
            {shouldUpdateCourseCard && (
              <CourseCard
                shouldHideCardImage={true}
                courseName={GOOGLE_COLAB}
                urlQuery={urlQuery}
              />
            )}
          </div>
        </div>
      </div>
      <FooterBanner
        title="Google Colab for kids with the best Computer Science teachers"
        url={`/register/${urlQuery}`}
      />
      <Footer />
    </>
  )
}

// TODO: Remove once getInitialProps is removed from _app.tsx
export const getStaticProps = async () => {
  return {
    props: {},
  }
}

export default GoogleColab
